import { parseNumberInput } from "@/lib/parse-number";

/**
 * Research poster arithmetic: paper sizes, type sizes for a viewing distance, a word
 * budget, and the numbers a print shop and a slide program need.
 *
 * What is exact: the ISO 216 A-series dimensions (A0 is 841 × 1189 mm, each size half
 * the area of the one above, ratio √2), inch ↔ millimetre conversion (25.4 mm), the
 * design-scale and enlargement arithmetic, and pixels = inches × dpi. What is guidance:
 * the legibility figures, which come from two published university guides and disagree
 * a little, and the word budget, which combines a layout rule of thumb with an estimate
 * of how much text a given type size holds. Those are labelled as such wherever they
 * are shown.
 */

export const MM_PER_INCH = 25.4;
export const FEET_PER_METRE = 1 / 0.3048;

export type SizeKey =
  | "a0"
  | "a1"
  | "a2"
  | "a3"
  | "in24x36"
  | "in30x40"
  | "in36x48"
  | "custom";

export type Orientation = "portrait" | "landscape";
export type LengthUnit = "in" | "cm";
export type DistanceUnit = "ft" | "m";

interface Preset {
  /** Width and height in millimetres, portrait (width ≤ height). */
  widthMm: number;
  heightMm: number;
}

/** ISO 216 A-series (rounded to whole millimetres, as the standard defines them) and common US sizes. */
export const SIZE_PRESETS: Record<Exclude<SizeKey, "custom">, Preset> = {
  a0: { widthMm: 841, heightMm: 1189 },
  a1: { widthMm: 594, heightMm: 841 },
  a2: { widthMm: 420, heightMm: 594 },
  a3: { widthMm: 297, heightMm: 420 },
  in24x36: { widthMm: 24 * MM_PER_INCH, heightMm: 36 * MM_PER_INCH },
  in30x40: { widthMm: 30 * MM_PER_INCH, heightMm: 40 * MM_PER_INCH },
  in36x48: { widthMm: 36 * MM_PER_INCH, heightMm: 48 * MM_PER_INCH },
};

export const POSTER_ROLES = [
  "title",
  "authors",
  "headings",
  "body",
  "captions",
] as const;
export type PosterRole = (typeof POSTER_ROLES)[number];

/** UC Davis Undergraduate Research Center's role sizes, in points; body is 24. */
export const ROLE_BASE_PT: Record<PosterRole, number> = {
  title: 85,
  authors: 56,
  headings: 36,
  body: 24,
  captions: 18,
};

/** UC Davis: "to be legible" at 6, 10, 12 and 14 feet. [feet, points] */
export const UC_DAVIS_POINTS: readonly (readonly [number, number])[] = [
  [6, 30],
  [10, 48],
  [12, 60],
  [14, 72],
];

/** Science Buddies, as quoted by Washington University's Knight ADRC: minimum comfortable size. */
export const SCIENCE_BUDDIES_POINTS: readonly (readonly [number, number])[] = [
  [1.2, 8],
  [2.4, 16],
  [5, 32],
  [25, 160],
];

export const MIN_BODY_PT = 24;
export const MAX_PPT_INCHES = 56;
export const TEXT_SHARE_DEFAULT = 20;
/** Purrington's range for a conference poster, in words. */
export const WORD_RANGE = { low: 500, high: 800 } as const;
/** Average silent reading speed for non-fiction in adults (Brysbaert, 2019). */
export const SILENT_WPM = 238;
export const DESIGN_SCALES = [1, 0.5, 0.25] as const;
export type DesignScale = (typeof DESIGN_SCALES)[number];

export const MIN_DISTANCE_FT = 1;
export const MAX_DISTANCE_FT = 40;

/* ------------------------------------------------------------------ size */

export interface PosterInput {
  size: SizeKey;
  orientation: Orientation;
  unit: LengthUnit;
  customWidth: string;
  customHeight: string;
  distance: string;
  distanceUnit: DistanceUnit;
  /** Blank: use the distance guide. */
  bodyPt: string;
  textShare: string;
  scale: DesignScale;
  imageWidth: string;
  imageHeight: string;
  dpi: string;
}

export function toMm(value: number, unit: LengthUnit): number {
  return unit === "in" ? value * MM_PER_INCH : value * 10;
}

export function fromMm(valueMm: number, unit: LengthUnit): number {
  return unit === "in" ? valueMm / MM_PER_INCH : valueMm / 10;
}

export interface PosterSize {
  widthMm: number;
  heightMm: number;
}

function orient(preset: Preset, orientation: Orientation): PosterSize {
  const [short, long] = [preset.widthMm, preset.heightMm].sort((a, b) => a - b);
  return orientation === "portrait"
    ? { widthMm: short, heightMm: long }
    : { widthMm: long, heightMm: short };
}

/** The poster's size as chosen, or null while a custom size is missing or not positive. */
export function resolvePosterSize(input: PosterInput): PosterSize | null {
  if (input.size !== "custom") {
    return orient(SIZE_PRESETS[input.size], input.orientation);
  }
  const width = parseNumberInput(input.customWidth);
  const height = parseNumberInput(input.customHeight);
  if (width === null || height === null || width <= 0 || height <= 0) {
    return null;
  }
  return {
    widthMm: toMm(width, input.unit),
    heightMm: toMm(height, input.unit),
  };
}

/* ------------------------------------------------------------------ type */

/**
 * Linear interpolation through a table of [x, y] points, proportional to the origin
 * below the first point and continuing the last segment's slope beyond the final one.
 */
export function interpolate(
  points: readonly (readonly [number, number])[],
  x: number
): number {
  const [firstX, firstY] = points[0];
  if (x <= firstX) {
    return (firstY / firstX) * x;
  }
  for (let i = 1; i < points.length; i += 1) {
    const [x0, y0] = points[i - 1];
    const [x1, y1] = points[i];
    if (x <= x1) {
      return y0 + ((y1 - y0) * (x - x0)) / (x1 - x0);
    }
  }
  const [xa, ya] = points.at(-2) ?? points[0];
  const [xb, yb] = points.at(-1) ?? points[0];
  return yb + ((yb - ya) / (xb - xa)) * (x - xb);
}

/** The viewing distance in feet, clamped to a sensible range; null for blank or invalid. */
export function distanceFeet(raw: string, unit: DistanceUnit): number | null {
  const value = parseNumberInput(raw);
  if (value === null || value <= 0) {
    return null;
  }
  const feet = unit === "ft" ? value : value * FEET_PER_METRE;
  return Math.min(MAX_DISTANCE_FT, Math.max(MIN_DISTANCE_FT, feet));
}

export interface LegibilityGuide {
  ucDavis: number;
  scienceBuddies: number;
}

export function legibilityAt(feet: number): LegibilityGuide {
  return {
    ucDavis: interpolate(UC_DAVIS_POINTS, feet),
    scienceBuddies: interpolate(SCIENCE_BUDDIES_POINTS, feet),
  };
}

/** The type size for each role, scaled from UC Davis's proportions to a chosen body size. */
export function roleSizes(bodyPt: number): Record<PosterRole, number> {
  const factor = bodyPt / ROLE_BASE_PT.body;
  return Object.fromEntries(
    POSTER_ROLES.map((role) => [role, Math.round(ROLE_BASE_PT[role] * factor)])
  ) as Record<PosterRole, number>;
}

/** A body size the writer typed, in points; null when blank or not a sensible size. */
export function bodyOverride(raw: string): number | null {
  const value = parseNumberInput(raw);
  if (value === null || value < 8 || value > 200) {
    return null;
  }
  return Math.round(value);
}

/* ------------------------------------------------------------------ words */

const LINE_HEIGHT = 1.2;
const CHARS_PER_WORD = 5.7;
const CHAR_WIDTH_EM = 0.5;
const POINTS_PER_INCH = 72;

/** Square inches one word and its space take at a type size, in a sans-serif of average width. */
export function wordAreaSquareInches(bodyPt: number): number {
  return (
    (LINE_HEIGHT * bodyPt * (CHARS_PER_WORD * CHAR_WIDTH_EM * bodyPt)) /
    POINTS_PER_INCH ** 2
  );
}

export function textShareFraction(raw: string): number {
  const value = parseNumberInput(raw);
  if (value === null || value < 5 || value > 100) {
    return TEXT_SHARE_DEFAULT / 100;
  }
  return value / 100;
}

export interface WordBudget {
  /** The estimated words the text share holds at the body size. */
  fit: number;
  /** The smaller of that and the top of the guideline range. */
  ceiling: number;
  /** Minutes to read the ceiling silently. */
  minutes: number;
}

export function wordBudget(
  areaSquareInches: number,
  share: number,
  bodyPt: number
): WordBudget {
  const fit = Math.floor(
    (areaSquareInches * share) / wordAreaSquareInches(bodyPt)
  );
  const ceiling = Math.min(fit, WORD_RANGE.high);
  return { fit, ceiling, minutes: ceiling / SILENT_WPM };
}

/* ------------------------------------------------------------------ print */

export interface DesignPlan {
  scale: DesignScale;
  widthIn: number;
  heightIn: number;
  /** Print enlargement in percent: 100 / scale. */
  enlargement: number;
  /** Whether PowerPoint can hold a slide this big (56 in on a side). */
  fitsPowerPoint: boolean;
  /** The largest design scale that fits PowerPoint, or null if none of the three does. */
  suggestedScale: DesignScale | null;
}

export function designPlan(size: PosterSize, scale: DesignScale): DesignPlan {
  const longIn = Math.max(size.widthMm, size.heightMm) / MM_PER_INCH;
  return {
    scale,
    widthIn: (size.widthMm / MM_PER_INCH) * scale,
    heightIn: (size.heightMm / MM_PER_INCH) * scale,
    enlargement: 100 / scale,
    fitsPowerPoint: longIn * scale <= MAX_PPT_INCHES,
    suggestedScale:
      DESIGN_SCALES.find((option) => longIn * option <= MAX_PPT_INCHES) ?? null,
  };
}

export const DPI_OPTIONS = ["100", "150", "200", "300"] as const;
export type Dpi = (typeof DPI_OPTIONS)[number];

export interface ImagePixels {
  widthPx: number;
  heightPx: number | null;
}

/** Pixels an image needs at its printed size: inches × dpi, rounded up. */
export function imagePixels(
  widthRaw: string,
  heightRaw: string,
  unit: LengthUnit,
  dpi: number
): ImagePixels | null {
  const width = parseNumberInput(widthRaw);
  if (width === null || width <= 0) {
    return null;
  }
  const height = parseNumberInput(heightRaw);
  const inches = (value: number) => toMm(value, unit) / MM_PER_INCH;
  return {
    widthPx: Math.ceil(inches(width) * dpi - 1e-9),
    heightPx:
      height !== null && height > 0
        ? Math.ceil(inches(height) * dpi - 1e-9)
        : null,
  };
}

/* ------------------------------------------------------------------ whole plan */

export interface PosterPlan {
  size: PosterSize | null;
  widthIn: number;
  heightIn: number;
  /** Long side over short side — √2 ≈ 1.4142 for the A-series. */
  ratio: number;
  areaSquareInches: number;
  feet: number | null;
  guide: LegibilityGuide | null;
  bodyPt: number | null;
  bodyFromGuide: boolean;
  roles: Record<PosterRole, number> | null;
  words: WordBudget | null;
  design: DesignPlan | null;
  image: ImagePixels | null;
}

export function planPoster(input: PosterInput): PosterPlan {
  const size = resolvePosterSize(input);
  const feet = distanceFeet(input.distance, input.distanceUnit);
  const guide = feet === null ? null : legibilityAt(feet);
  const override = bodyOverride(input.bodyPt);
  let bodyPt: number | null = override;
  if (bodyPt === null && guide !== null) {
    bodyPt = Math.max(MIN_BODY_PT, Math.round(guide.ucDavis));
  }
  const widthIn = size ? size.widthMm / MM_PER_INCH : 0;
  const heightIn = size ? size.heightMm / MM_PER_INCH : 0;
  const area = widthIn * heightIn;
  const dpi = Number.parseInt(input.dpi, 10) || 300;
  return {
    size,
    widthIn,
    heightIn,
    ratio:
      size && Math.min(widthIn, heightIn) > 0
        ? Math.max(widthIn, heightIn) / Math.min(widthIn, heightIn)
        : 0,
    areaSquareInches: area,
    feet,
    guide,
    bodyPt,
    bodyFromGuide: override === null,
    roles: bodyPt === null ? null : roleSizes(bodyPt),
    words:
      size && bodyPt !== null
        ? wordBudget(area, textShareFraction(input.textShare), bodyPt)
        : null,
    design: size ? designPlan(size, input.scale) : null,
    image: imagePixels(input.imageWidth, input.imageHeight, input.unit, dpi),
  };
}
